import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
  onSwitchToRegister: () => void;
  translations: any;
  handleGoogleButtonClick: () => void;
}

export const LoginDialog = ({
  open,
  onClose,
  onSuccess,
  onSwitchToRegister,
  translations: t,
  handleGoogleButtonClick,
}: LoginDialogProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });
  const { login } = useAuth();

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const { data } = await axios.post('/api/auth', loginForm);
      
      login(data.user, data.token);
      onSuccess(data.user);

      if (data.user.role === 'admin') {
        navigate('/admin');
      }
      
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontFamily: 'Merriweather, serif', pb: 1 }}>
        {t.signin}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleLoginSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleButtonClick}
            sx={{ mb: 2 }}
          >
            {t.signinWithGoogle}
          </Button>
          <Divider sx={{ my: 2 }}>or</Divider>
          <TextField
              autoFocus
              margin="dense"
              name="email"
              label={t.email}
              type="email"
              fullWidth
              variant="outlined"
              value={loginForm.email}
              onChange={handleLoginChange}
              required
              disabled={loading}
            />
            <TextField
              margin="dense"
              name="password"
              label={t.password}
              type="password"
              fullWidth
              variant="outlined"
              value={loginForm.password}
              onChange={handleLoginChange}
              required
              disabled={loading}
            />
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', p: 3, pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: '#382110',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#58371F',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t.signin}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t.newToKtebstan} {' '}
                <Button
                  onClick={() => {
                    onClose();
                    onSwitchToRegister();
                  }}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 'normal',
                    p: 0,
                    ml: 0.5,
                  }}
                >
                  {t.createAnAccount}
                </Button>
              </Typography>
            </Box>
          </DialogActions>
        </form>
    </Dialog>
  );
}; 